import type { Directive } from 'vue'

type TargetOption = 'root' | 'body' | string | HTMLElement
export interface SafeAreaOptions {
  // Optional explicit variable name, e.g. "--safe-area-top". If omitted, we derive from `name`.
  varName?: string
  // Area name. Accepts "top" | "bottom" | any string. If omitted, defaults to "top".
  name?: string
  // Also set `--header-height` with the measured px height (default: true)
  setHeaderVar?: boolean
  // If true, for top/bottom areas we set value to `max(height, env(safe-area-inset-<area>))` (default: false)
  withDeviceInset?: boolean
  // Where to set the var: 'root' | 'body' | CSS selector | element (default: 'root')
  target?: TargetOption
  // Debounce (rAF) changes (default: true)
  raf?: boolean
}

function resolveTarget(t?: TargetOption): HTMLElement {
  if (typeof window === 'undefined') return undefined as any
  if (!t || t === 'root') return document.documentElement
  if (t === 'body') return document.body
  if (typeof t === 'string') return document.querySelector(t) ?? document.documentElement
  return t
}

export const vSafeArea: Directive<HTMLElement, SafeAreaOptions | string | undefined> = {
  mounted(el, binding) {
    if (typeof window === 'undefined') return

    // Normalize binding into options
    const input = binding?.value
    const argArea = typeof binding?.arg === 'string' && binding.arg.length ? binding.arg : undefined
    const mods = binding?.modifiers ?? {}
    const modArea = mods.top ? 'top' : (mods.bottom ? 'bottom' : undefined)
    const nameFromBinding = argArea
      ?? modArea
      ?? (typeof input === 'string'
        ? input
        : (input && typeof input === 'object' && typeof (input as any).name === 'string')
          ? (input as any).name
          : undefined)

    const normalizedArea = (nameFromBinding ?? 'top').toString().trim().toLowerCase()
    const isTop = normalizedArea === 'top'
    const isBottom = normalizedArea === 'bottom'
    const derivedVarName = `--safe-area-${isTop ? 'top' : isBottom ? 'bottom' : normalizedArea}`

    const opts: Required<SafeAreaOptions> = {
      varName: typeof input === 'object' && input && 'varName' in input && typeof (input as any).varName === 'string'
        ? (input as any).varName
        : derivedVarName,
      name: normalizedArea,
      setHeaderVar: typeof input === 'object' && input && 'setHeaderVar' in input
        ? Boolean((input as any).setHeaderVar)
        : true,
      withDeviceInset: typeof input === 'object' && input && 'withDeviceInset' in input
        ? Boolean((input as any).withDeviceInset)
        : false,
      target: typeof input === 'object' && input && 'target' in input
        ? (input as any).target ?? 'root'
        : 'root',
      raf: typeof input === 'object' && input && 'raf' in input
        ? Boolean((input as any).raf)
        : true,
    }

    const target = resolveTarget(opts.target)

    let ro: ResizeObserver | null = null
    let frame = 0

    const write = () => {
      frame = 0
      const h = Math.max(0, Math.round(el.getBoundingClientRect().height))
      if (opts.setHeaderVar) {
        target.style.setProperty('--header-height', `${h}px`)
      }
      const envVar = opts.name === 'top' ? 'safe-area-inset-top' : opts.name === 'bottom' ? 'safe-area-inset-bottom' : null
      const value = opts.withDeviceInset && envVar
        ? `max(${h}px, env(${envVar}))`
        : `${h}px`
      target.style.setProperty(opts.varName, value)
    }

    const schedule = () => {
      if (!opts.raf) return write()
      if (frame) return
      frame = requestAnimationFrame(write)
    }

    if ('ResizeObserver' in globalThis) {
      ro = new ResizeObserver(schedule)
      ro.observe(el)
    } else {
      globalThis.addEventListener('resize', schedule)
    }

    schedule()

    ;(el as any).__safeArea__ = { ro, schedule, frame, opts, target }
  },

  updated(el) {
    const s = (el as any).__safeArea__
    if (s) s.schedule()
  },

  unmounted(el) {
    const s = (el as any).__safeArea__
    if (!s) return
    if (s.ro) s.ro.disconnect()
    window.removeEventListener('resize', s.schedule)
    if (s.frame) cancelAnimationFrame(s.frame)
    delete (el as any).__safeArea__
  },
}